import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { fieldAnatomyStyles, fieldMessage, nextFieldMessageId, renderFieldLabel, renderFieldMessage } from './field-anatomy.js';
import { fieldHelpDockActiveContext } from './field-help-dock-context.js';

/**
 * <cos-textarea> — the house multi-line text field.
 *
 * WHY THIS EXISTS: `cos-input`'s sibling for anything longer than one
 * line — same label/help/error/disabled/required anatomy and control
 * geometry, so the two read as a matched pair in a form.
 *
 * Controlled, like `cos-input`: `value` is the prop the consumer owns;
 * the component echoes the DOM value back onto it on every keystroke
 * (firing `cos:input`) and fires `cos:change` on commit (blur only —
 * Enter inserts a newline here, it does not commit).
 *
 * Props:
 *   - value: string — controlled
 *   - label?, placeholder?, helpText?
 *   - error?: string — replaces helpText with error styling + message,
 *     marks the field aria-invalid
 *   - required?, disabled?
 *   - size: sm|md (default md)
 *   - name?
 *   - rows?: number (default 3) — starting height in rows
 *   - autoResize?: boolean — when true, the control grows with its
 *     content up to a capped max height, then scrolls internally
 *   - hideLabel?: boolean (wave 3, ds-unlocks) — same contract as
 *     cos-input's: `label` stays required as the accessible name, the
 *     visible caption clips off-screen.
 *   - maxlength?: number (wave 6, ds-unlocks) — plain passthrough to the
 *     inner `<textarea maxlength>`; 0 (default) omits the attribute.
 *
 * Emits `cos:input` (detail: { value }) on every keystroke and
 * `cos:change` (detail: { value }) on commit (blur).
 */
@customElement('cos-textarea')
export class CosTextarea extends LitElement {
	@property({ type: String }) value = '';
	@property({ type: String }) label = '';
	@property({ type: String }) placeholder = '';
	@property({ type: String }) helpText = '';
	@property({ type: String }) error = '';
	@property({ type: Boolean }) required = false;
	@property({ type: Boolean, reflect: true }) disabled = false;
	// Read-only keeps the text selectable and copyable, which disabled does not.
	@property({ type: Boolean, reflect: true }) readonly = false;
	@property({ type: String, reflect: true }) size: 'sm' | 'md' = 'md';
	@property({ type: String }) name = '';
	@property({ type: Number }) rows = 3;
	@property({ type: Boolean }) autoResize = false;
	@property({ type: Boolean, attribute: 'hide-label' }) hideLabel = false;
	@property({ type: Number }) maxlength = 0;

	@consume({ context: fieldHelpDockActiveContext, subscribe: true })
	@state()
	private _dockActive = false;

	@query('textarea') private _textareaEl?: HTMLTextAreaElement;

	// Capped growth for autoResize — beyond this the control scrolls
	// internally rather than pushing the rest of the page around.
	private static readonly MAX_AUTO_HEIGHT_PX = 320;

	private _focusValue = '';

	private readonly _msgId = nextFieldMessageId('cos-textarea');

	focus(): void {
		this._textareaEl?.focus();
	}

	private _onFocus(): void {
		this._focusValue = this.value;
	}

	private _onInput(e: Event): void {
		this.value = (e.target as HTMLTextAreaElement).value;
		this._resize(e.target as HTMLTextAreaElement);
		this.dispatchEvent(
			new CustomEvent('cos:input', { detail: { value: this.value }, bubbles: true, composed: true }),
		);
	}

	private _resize(el: HTMLTextAreaElement): void {
		if (!this.autoResize) return;
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, CosTextarea.MAX_AUTO_HEIGHT_PX)}px`;
	}

	private _commit(): void {
		if (this.value === this._focusValue) return;
		this._focusValue = this.value;
		this.dispatchEvent(
			new CustomEvent('cos:change', { detail: { value: this.value }, bubbles: true, composed: true }),
		);
	}

	protected firstUpdated(): void {
		if (this._textareaEl) this._resize(this._textareaEl);
	}

	static styles = css`
		${fieldAnatomyStyles}
		:host {
			display: block;
			width: 100%;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-primary);
		}
		.field {
			display: flex;
			flex-direction: column;
			gap: 6px;
			width: 100%;
		}
		.control {
			width: 100%;
			box-sizing: border-box;
			padding: 9px 12px;
			font: inherit;
			color: var(--cos-text-primary);
			background: var(--cos-surface);
			border: 1.5px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
			resize: vertical;
		}
		:host([size='sm']) .control {
			padding: 7px 12px;
			font-size: var(--cos-font-size-xs);
		}
		.control[data-auto-resize] {
			resize: none;
			overflow-y: auto;
		}
		.control::placeholder {
			color: var(--cos-text-tertiary);
		}
		.control:hover:not(:disabled) {
			border-color: var(--cos-brand-500);
		}
		.control:focus-visible {
			outline: none;
			border-color: var(--cos-brand-700);
			box-shadow: var(--cos-shadow-focus);
		}
		.control[aria-invalid='true'] {
			border-color: var(--cos-status-danger-text);
		}
		.control:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
		/* Workstream A1 — see cos-input's identical rule for the full
		   rationale (iOS Safari's forced focus-zoom below 16px) and why the
		   size='sm' selector is repeated here at matching specificity. */
		@media (pointer: coarse) {
			.control,
			:host([size='sm']) .control {
				font-size: max(16px, var(--cos-font-size-base));
			}
		}
	`;

	render() {
		const { message, isError } = fieldMessage(this.helpText, this.error);
		return html`
			<label class="field">
				${renderFieldLabel({ label: this.label, required: this.required, hideLabel: this.hideLabel })}
				<textarea
					class="control"
					name=${this.name || nothing}
					placeholder=${this.placeholder || nothing}
					maxlength=${this.maxlength || nothing}
					rows=${this.rows}
					?data-auto-resize=${this.autoResize}
					.value=${this.value}
					?required=${this.required}
					?disabled=${this.disabled}
						?readonly=${this.readonly}
					aria-invalid=${this.error ? 'true' : 'false'}
					aria-describedby=${message ? this._msgId : nothing}
					@focus=${this._onFocus}
					@input=${this._onInput}
					@blur=${this._commit}
				></textarea>
				${renderFieldMessage({ message, isError, id: message ? this._msgId : undefined, visuallyHidden: this._dockActive && !isError })}
			</label>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-textarea': CosTextarea;
	}
}
